import type { AuthErrorType } from '../components/auth-error-display/auth-error-display.component';

/**
 * What the Support panel is told about the error the user was looking at when
 * they asked for help, so it can offer to copy the details into a chat.
 */
export interface AuthErrorSupportContext {
  source: 'auth-error';
  type: AuthErrorType;
  title: string;
  message: string;
  attemptedUrl?: string;
  createdAt: string;
}

declare global {
  interface Window {
    tracitSupportContext?: AuthErrorSupportContext;
  }
}

/**
 * Opens the Support panel, tagged with the error that prompted it.
 *
 * The panel lives in index.html, outside the Angular app, so it is driven
 * through the DOM. Kept here rather than in either caller because both the
 * auth error modal and the sign-in page need it, and because the global Window
 * declaration above has to sit in a module that every caller imports.
 */
export function openSupportPanel(doc: Document, context: AuthErrorSupportContext): void {
  const view = doc.defaultView;

  if (!view) {
    return;
  }

  view.tracitSupportContext = context;

  // Deferred to the next task, and not optional. The panel closes itself on any
  // click landing outside it, so clicking the toggle synchronously from within
  // another element's click handler opens the panel and then lets that same
  // event carry on bubbling to the document, which closes it again. Waiting for
  // the current event to finish means the panel opens and stays open.
  view.setTimeout(() => doc.getElementById('support-toggle')?.click());
}
